/**
 * ScoreRing — an animated circular gauge for a 0-100 prospect score.
 * The arc sweeps in on mount and is coloured on the cold→hot scale.
 */
import { motion } from 'framer-motion';
import { scoreColor } from '@/lib/prospectMeta';

interface ScoreRingProps {
  score: number;
  /** Diameter in px. */
  size?: number;
  /** Caption shown under the number. */
  caption?: string;
}

export function ScoreRing({ score, size = 132, caption }: ScoreRingProps) {
  const stroke = size * 0.085;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, score));
  const dash = (clamped / 100) * circumference;
  const color = scoreColor(clamped);

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        {/* Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="text-ink/[0.07]"
        />
        {/* Animated value arc */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference - dash }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          style={{ filter: `drop-shadow(0 0 6px ${color}66)` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="font-display font-800 tracking-[-0.03em]"
          style={{ fontSize: size * 0.3, color }}
        >
          {clamped}
        </motion.span>
        {caption && (
          <span className="mt-0.5 font-mono text-[9px] font-700 uppercase tracking-[0.14em] text-ink/45">
            {caption}
          </span>
        )}
      </div>
    </div>
  );
}
